'use client';

import { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { LABELS } from '@/lib/labels';

function isActive(href: string, pathname: string): boolean {
  if (href === '/') return pathname === '/';
  if (href.startsWith('/#')) return false;
  return pathname.startsWith(href.replace(/\/$/, ''));
}

const NAV_ITEMS = [
  { href: '/', label: LABELS.nav.home },
  { href: '/calendario/', label: LABELS.nav.calendario },
  { href: '/#faq', label: LABELS.nav.faq },
] as const;

export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const pathname = usePathname();

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-chalk/10 shadow-sm">
      <div className="max-w-5xl mx-auto px-4 h-16 flex items-center justify-between">

        {/* Logo */}
        <Link href="/" className="flex items-center hover:opacity-80 transition-opacity" aria-label="Terzo Tempo - Home">
          <Image
            src="/_images/terzo-tempo-logo.webp"
            alt="Terzo Tempo"
            width={160}
            height={64}
            className="h-10 w-auto"
            style={{ mixBlendMode: 'multiply' }}
          />
        </Link>

        {/* Nav desktop con separatori */}
        <nav className="hidden md:flex items-center gap-0" aria-label={LABELS.nav.mainNav}>
          {NAV_ITEMS.map(({ href, label }, i, arr) => (
            <span key={href} className="flex items-center">
              <Link
                href={href}
                className={`px-4 transition-colors text-sm font-bold uppercase tracking-wide ${
                  isActive(href, pathname) ? 'text-accent-orange' : 'text-chalk hover:text-accent-orange'
                }`}
              >
                {label}
              </Link>
              {i < arr.length - 1 && (
                <span className="text-chalk/30 text-sm select-none" aria-hidden="true">|</span>
              )}
            </span>
          ))}
        </nav>

        {/* Hamburger mobile */}
        <button
          className="md:hidden text-chalk p-2 rounded hover:bg-chalk/10 transition-colors"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-expanded={menuOpen}
          aria-controls="mobile-menu"
          aria-label={menuOpen ? LABELS.nav.menuChiudi : LABELS.nav.menuApri}
        >
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            {menuOpen ? (
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            )}
          </svg>
        </button>
      </div>

      {menuOpen && (
        <nav id="mobile-menu" className="md:hidden bg-white border-t border-chalk/10 px-4 py-4 space-y-3" aria-label={LABELS.nav.mobileMenu}>
          {NAV_ITEMS.map(({ href, label }) => (
            <Link
              key={href}
              href={href}
              className={`block transition-colors py-1 text-sm uppercase tracking-wide font-bold ${
                isActive(href, pathname) ? 'text-accent-orange' : 'text-chalk hover:text-accent-orange'
              }`}
              onClick={() => setMenuOpen(false)}
            >
              {label}
            </Link>
          ))}
        </nav>
      )}
    </header>
  );
}